import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ScanResult {
  ok: boolean;
  dry_run: boolean;
  scanned: number;
  referenced: number;
  orphans: number;
  deleted: number;
  sample: string[];
}

export function LibraryCoverOrphansPanel() {
  const [loading, setLoading] = useState<"idle" | "scan" | "delete">("idle");
  const [result, setResult] = useState<ScanResult | null>(null);

  const run = async (confirm: boolean) => {
    setLoading(confirm ? "delete" : "scan");
    try {
      const { data, error } = await supabase.functions.invoke<ScanResult>(
        "library-covers-orphan-cleanup",
        { body: { confirm } },
      );
      if (error) throw error;
      if (!data) throw new Error("Empty response");
      setResult(data);
      toast.success(
        confirm
          ? `Deleted ${data.deleted} orphan${data.deleted === 1 ? "" : "s"}`
          : `Scanned ${data.scanned} files, ${data.orphans} orphan${data.orphans === 1 ? "" : "s"}`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Cleanup failed");
    } finally {
      setLoading("idle");
    }
  };

  const canDelete = !!result && result.dry_run && result.orphans > 0;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Library cover orphans</CardTitle>
        <CardDescription>
          One-shot cleanup of files in the <code>library-covers</code> bucket that are no longer
          referenced by any <code>library_items.cover_url</code>. Run a scan first; deletion is
          gated behind a confirm step.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(false)} disabled={loading !== "idle"} variant="outline">
            {loading === "scan" ? "Scanning…" : "Scan (dry run)"}
          </Button>
          <Button
            onClick={() => {
              if (!result || result.orphans === 0) return;
              if (!confirm(`Delete ${result.orphans} orphaned cover file(s)? This cannot be undone.`)) return;
              run(true);
            }}
            disabled={loading !== "idle" || !canDelete}
            variant="destructive"
          >
            {loading === "delete" ? "Deleting…" : "Delete orphans"}
          </Button>
        </div>

        {result && (
          <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Scanned" value={result.scanned} />
              <Stat label="Referenced" value={result.referenced} />
              <Stat label="Orphans" value={result.orphans} />
              <Stat label="Deleted" value={result.deleted} />
            </div>
            {result.sample.length > 0 && (
              <div>
                <div className="font-medium text-muted-foreground mb-1">
                  Sample paths ({result.sample.length}):
                </div>
                <ul className="space-y-0.5 font-mono text-xs text-muted-foreground">
                  {result.sample.map((p) => (
                    <li key={p} className="truncate">{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
